import {
	computed,
	type ComputedRef,
	onMounted,
	ref,
	type Ref,
	watch,
} from 'vue'
import { get } from 'idb-keyval'
import { clone, cloneDeep, debounce } from 'lodash'
import {
	keepPreviousData,
	type QueryKey,
	useIsFetching,
	useQueries,
	useQuery,
	useQueryClient,
} from '@tanstack/vue-query'
import { fetchCollectionData } from '@/services/api/collections'
import DocumentService from '@/helpers/services/DocumentService.ts'
import GenericEntityService from '@/helpers/services/GenericEntityService.ts'
import type {
	DataTableDefinition,
	DataTablePaginate,
	SortItem,
	useTableDefinition,
} from '@/types/table.ts'
import type { FilterDefinition } from '@/constants/filters'
import type { Filter } from '@/stores/system'
import { storeToRefs } from 'pinia'
import { useTableStateStore } from '@/stores/system/tableState.ts'
import { useUserStore } from '@/stores/system/user.ts'
import { persister } from 'plugins/queryClient'

export interface UseTableOptions {
	definition: DataTableDefinition
	endpoint?: string | ComputedRef<string>
	//TODO: propData is currently not reactive, needs to be fixed
	propData?: any[]
	params?: Record<string, any> | ComputedRef<Record<string, any>>
	filters?: FilterDefinition[]
	sortBy?: SortItem[]
	paginate?: DataTablePaginate
	name?: string
	module?: string
	preloadAllPages?: boolean
	tableId?: string | ComputedRef<string | undefined> | Ref<string> // Support string, computed, and ref
	usePlaceHolderData?: boolean
	disabled?: ComputedRef<boolean>
	usePersister?: boolean
	/**
	 * Dashboard-Widgets laden Seiten nach (useWidgetPaging): Verschiebt sich die Liste zwischen zwei
	 * Seitenabrufen, taucht eine Zeile in zwei Seiten auf. Mit dieser Option zählt in der
	 * zusammengeführten Liste jede `id` nur einmal (erste Fundstelle).
	 */
	dedupeById?: boolean
	/**
	 * TanStack Query retry count / predicate for this table's queries.
	 * When set, overrides the global QueryClient default for both the main
	 * page query and every all-pages observer.
	 *
	 * Typical use: `retry: 4` on protocol / batch-preload tables so transient
	 * 5xx / Network-Error failures heal automatically.
	 */
	retry?: boolean | number
	/**
	 * TanStack Query retry delay.  Accepts a fixed millisecond value or a
	 * function `(attempt, error) => ms` for exponential back-off.
	 *
	 * Example: `(attempt) => Math.min(1000 * 2 ** attempt, 5000)` gives
	 * 2 s → 4 s → 5 s caps for attempts 1–3.
	 */
	retryDelay?: number | ((attempt: number, error: Error) => number)
	/**
	 * When true, every axios request issued by this table's query functions
	 * will carry `meta.silentError = true`, which instructs `responseOnRejected`
	 * to skip the global alert dialog and router redirects for that request.
	 * The query layer still receives the rejection and retries normally.
	 *
	 * Strictly opt-in — all other callers retain the default error handling.
	 */
	silentError?: boolean
}

export interface tableId {
	id: string
	module: string
	endpoint: string
}

export function generateTableId(
	endpoint: string,
	module?: string,
	name?: string
): tableId {
	const usedModule = mapModule(module, endpoint)
	name = name ? `-${name}` : ''
	return {
		id: `${usedModule}-${endpoint}${name}`,
		module: usedModule,
		endpoint,
	}
}

const mapEndpoint = (
	endpoint: string | ComputedRef<string>,
	split: boolean = true
) => {
	let usedEndpoint =
		typeof endpoint === 'string' ? endpoint : endpoint?.value || ''
	if (usedEndpoint.includes('?') && split) {
		usedEndpoint = usedEndpoint.split('?')[0]
	}
	return usedEndpoint
}
const mapModule = (module: string | undefined, endpoint: string) => {
	if (typeof module !== 'undefined') {
		return module
	}
	return endpoint.split('/').pop()?.split('?')[0] || ''
}

export function useTable(
	tableOptions: Partial<UseTableOptions>
): useTableDefinition {
	const tableStateStore = useTableStateStore()
	const queryClient = useQueryClient()
	const { isOnline } = storeToRefs(useUserStore())

	const tableId = computed({
		get: () => {
			let providedTableId: string | undefined

			if (tableOptions.tableId) {
				if (typeof tableOptions.tableId === 'string') {
					providedTableId = tableOptions.tableId
				} else if ('value' in tableOptions.tableId) {
					providedTableId = tableOptions.tableId.value
				}
			}

			if (providedTableId) {
				return providedTableId
			}

			if (
				!tableOptions.definition?.endpoint &&
				!tableOptions.endpoint &&
				tableOptions.disabled?.value !== true
			) {
				throw new Error(
					'Table ID cannot be generated without an endpoint or definition.'
				)
			}
			const { id } = generateTableId(
				mapEndpoint(tableOptions.endpoint || tableOptions.definition?.endpoint),
				tableOptions.module,
				tableOptions.name
			)
			return id
		},
		set: (value) => {
			// Update the tableId in the options
			if (typeof tableOptions.tableId === 'string') {
				tableOptions.tableId = value
			} else if (tableOptions.tableId && 'value' in tableOptions.tableId) {
				// It's a ref, update its value
				tableOptions.tableId.value = value
			} else {
				// Create a new ref
				tableOptions.tableId = value
			}
		},
	})
	const params = computed(() => {
		if (
			typeof tableOptions.params === 'undefined' ||
			tableOptions.params === null
		) {
			return {}
		}

		// Handle computed ref params
		const paramsValue =
			'value' in tableOptions.params
				? tableOptions.params.value
				: tableOptions.params

		if (
			typeof paramsValue === 'undefined' ||
			Object.keys(paramsValue).length === 0
		) {
			return {}
		}

		const paramsCopy = clone(paramsValue)
		const reservedKeyWords = [
			'endpoint',
			'pageSize',
			'pageNum',
			'of',
			'od',
			'search',
		]

		for (const key in paramsCopy) {
			if (!reservedKeyWords.includes(key)) {
				continue
			}
			delete paramsCopy[key]
		}
		return paramsCopy
	})
	const initialState = {
		page: 1,
		itemsPerPage: tableOptions.paginate?.perPage,
		search: '',
		sortBy:
			tableOptions.sortBy ||
			tableOptions.definition?.sortBy ||
			(tableOptions.params?.of
				? [
						{
							key: tableOptions.params?.of ?? 'id',
							order: tableOptions.params?.od || 'asc',
						},
					]
				: [{ key: 'id', order: 'desc' }]),
		expandedItems: [],
		selectedItems: [],
		filters: (
			tableOptions.filters ||
			tableOptions.definition?.filters ||
			[]
		).map((filter) => ({
			definition: filter,
			value: undefined,
		})),
	}

	// Get stored state
	const storedState = computed(() =>
		tableStateStore.getTableState(tableId.value)
	)

	// Table state with reactivity
	const page = computed({
		get: () => storedState.value.page,
		set: (value) => tableStateStore.updatePagination(tableId.value, value),
	})

	const itemsPerPage = computed({
		get: () => {
			if (typeof storedState.value.itemsPerPage !== 'undefined') {
				return storedState.value.itemsPerPage
			}
			return tableOptions.paginate?.perPage || 25
		},
		set: (value) =>
			tableStateStore.updatePagination(tableId.value, page.value, value),
	})

	const search = computed({
		get: () => storedState.value.search,
		set: debounce((value) => {
			//TODO: Should i validate a min string length?
			tableStateStore.updateSearch(tableId.value, value)
		}, 500),
	})

	const sortByValues = computed({
		get: () => storedState.value.sortBy,
		set: (value) => tableStateStore.updateSorting(tableId.value, value),
	})

	const filters = computed({
		get: () => {
			if (storedState.value.filters.length > 0) {
				return storedState.value.filters
			}
			const filterDefs =
				tableOptions.filters || tableOptions.definition?.filters || []
			return filterDefs.map((filter) => {
				return {
					definition: filter,
					value: undefined,
				}
			})
		},
		set: (value: Filter[]) =>
			tableStateStore.updateFilters(tableId.value, value),
	})

	const expandedItems = computed({
		get: () => storedState.value.expandedItems,
		set: (value) => tableStateStore.updateExpandedItems(tableId.value, value),
	})

	const selectedItems = computed({
		get: () => storedState.value.selectedItems,
		set: (value) => tableStateStore.updateSelectedItems(tableId.value, value),
	})

	const hasSelectedItems = computed(() => selectedItems.value.length > 0)

	const usePlaceHolderData = computed(() => {
		return (
			tableOptions.usePlaceHolderData ||
			tableOptions.definition?.usePlaceHolderData ||
			false
		)
	})

	// Query parameters
	const sortField = computed(() => sortByValues.value[0]?.key)

	// `sort` auf einer Spalte ist ein Alias für das Feld, nach dem die API sortiert
	// (z.B. report_status -> report_status.ordering). Nur für die Server-Query — der
	// In-Memory-Pfad (propData) sortiert weiter über den rohen Spalten-Key.
	const sortFieldApi = computed(() => {
		const key = sortField.value
		if (!key) return key
		return (
			tableOptions.definition?.columns?.find((column) => column.key === key)
				?.sort || key
		)
	})

	const sortOrder = computed(() => sortByValues.value[0]?.order)

	const filtersFormatted = computed(() =>
		tableStateStore.getFiltersValueFormatted(tableId.value)
	)

	const endpointUsed = computed(() =>
		mapEndpoint(
			tableOptions.endpoint ||
				tableOptions.definition?.endpoint ||
				storedState.value.endpoint,
			false
		)
	)

	init()

	const queryParams = computed(() => {
		const relations = tableOptions.definition?.relations
			? { rel: tableOptions.definition.relations }
			: params.value.rel
				? { rel: params.value.rel }
				: {}
		return {
			pageNum: page.value,
			pageSize: itemsPerPage.value,
			of: sortFieldApi.value,
			od: sortOrder.value,
			s:
				typeof search.value === 'string' && search.value.length > 0
					? search.value
					: null,
			sm: filtersFormatted.value ? 1 : 0,
			...params.value,
			...relations,
			...(filtersFormatted.value || undefined),
		}
	})

	// Error state
	const error = ref(null)

	// Special handling for propData with pagination
	const paginatedPropData = computed(() => {
		if (!tableOptions.propData?.value) return []

		// Apply search filter if needed
		let filteredData = tableOptions.propData?.value
		if (search.value) {
			const searchLower = search.value.toLowerCase()
			//TODO: should be refactored cause duplicate
			filteredData = tableOptions.propData?.value.filter((item) => {
				// Search in all string and number fields
				return Object.values(item).some((value) => {
					if (typeof value === 'string') {
						return value.toLowerCase().includes(searchLower)
					} else if (typeof value === 'number') {
						return value.toString().includes(searchLower)
					}
					return false
				})
			})
		}

		// Apply sorting if needed
		if (sortField.value) {
			const field = sortField.value
			const direction = sortOrder.value === 'desc' ? -1 : 1

			filteredData = [...filteredData].sort((a, b) => {
				const valueA = a[field]
				const valueB = b[field]

				// Handle different types
				if (typeof valueA === 'string' && typeof valueB === 'string') {
					return direction * valueA.localeCompare(valueB)
				} else {
					if (valueA < valueB) return -1 * direction
					if (valueA > valueB) return 1 * direction
					return 0
				}
			})
		}

		// Apply pagination
		const start = (page.value - 1) * itemsPerPage.value
		const end = start + itemsPerPage.value
		return filteredData.slice(start, end)
	})

	const propDataTotal = computed(() => {
		if (!tableOptions.propData?.value) return 0

		// Apply search filter if needed
		if (search.value) {
			const searchLower = search.value.toLowerCase()

			//TODO: should be refactored cause duplicate
			return tableOptions.propData?.value.filter((item) => {
				// Search in all string and number fields
				return Object.values(item).some((value) => {
					if (typeof value === 'string') {
						return value.toLowerCase().includes(searchLower)
					} else if (typeof value === 'number') {
						return value.toString().includes(searchLower)
					}
					return false
				})
			}).length
		}

		return tableOptions.propData?.value.length
	})

	// TanStack Query e2e (only when not using propData)
	//TODO: pop works for buildings/{id}/reports but not for notifications/unread
	const module = computed(() =>
		tableOptions.module
			? tableOptions.module
			: mapEndpoint(
					endpointUsed.value.includes('/')
						? endpointUsed.value.split('/').pop()
						: endpointUsed.value
				)
	)

	// Create a page query key that matches the format used by our main query
	const createPageQueryKey = (pageNumber: number) => {
		return [
			module.value,
			{
				endpoint: endpointUsed.value,
				pageNum: pageNumber,
				pageSize: itemsPerPage.value,
				of: sortField.value,
				od: sortOrder.value,
				filters: filtersFormatted.value,
				search: search.value,
				params: params.value,
				/*name: tableOptions.name,*/
			},
		]
	}

	const queryKey = computed((): QueryKey => createPageQueryKey(page.value))

	const mapDbData = async (currentPage?: number) => {
		if (!Array.isArray(tableOptions.definition?.relatesTo)) {
			console.error(`Error fetching data for table ${tableId.value}:`, error)
		}
		const regex = /([a-zA-Z_-]+)\/(\d+)\/([a-zA-Z_-]+)\/?\S*$/
		const match = endpointUsed.value.match(regex)

		if (!match) {
			return null
		}
		const module = match[1] // "persons"
		const id = match[2] // "647"
		const relation = match[3]
		const documentService = new DocumentService(get)
		if (module === 'buildings') {
			if (relation === 'documents') {
				return await documentService.getItems(
					{ type: 'building', id: Number(id) },
					{ perPage: tableOptions.paginate?.perPage, currentPage }
				)
			}
		}
		if (module === 'persons') {
			if (relation === 'documents') {
				return await documentService.getItems(
					{ type: 'person', id: Number(id) },
					{ perPage: tableOptions.paginate?.perPage, currentPage }
				)
			}
		}

		const relatedEntity = tableOptions.definition?.relatesTo?.find(
			(entry) => entry.collection === module
		)
		if (!relatedEntity) {
			console.warn(
				`No related entity found for module ${module} in table definition`
			)
			return null
		}
		return new GenericEntityService(get, [
			{
				collection: 'phonenumbers',
				column: 'person_id',
				singleItem: false,
			},
			{
				collection: 'emails',
				column: 'person_id',
				singleItem: false,
			},
			{
				collection: 'permissions',
				column: 'person_id',
				singleItem: false,
			},
		]).getItems({
			type: relation,
			id: Number(id),
		})
	}

	// Query (only when not using propData)
	const {
		data,
		error: queryError,
		refetch,
		fetchStatus,
		isLoading: isInitialLoading,
		// `keepPreviousData` liefert nach einem Schlüsselwechsel weiter die Daten
		// des vorherigen Schlüssels. Wer eine Kennzahl aus `queryData` liest, muss
		// unterscheiden können, ob die Antwort zum aktuellen Schlüssel schon da ist
		// — sonst zeigt er eine fremde Zahl als seine eigene an.
		isPlaceholderData,
		promise: fetchPromise,
	} = useQuery({
		queryKey,
		persister:
			tableOptions.usePersister === true || !isOnline.value
				? persister.persisterFn
				: undefined,
		experimental_prefetchInRender: true,
		queryFn: async () => {
			try {
				if (!isEnabled()) {
					return null
				}
				return await fetchCollectionData(
					endpointUsed.value,
					queryParams.value,
					tableOptions.silentError ? { meta: { silentError: true } } : undefined
				)
			} catch (error) {
				if (
					error.message === 'Network Error' &&
					process.env.NODE_ENV === 'development'
				) {
					return await mapDbData(page.value)
				}
				throw error
			}
		},
		enabled: isEnabled,
		staleTime: (import.meta.env.VITE_QUERY_STALE_TIME || 5) * 60 * 1000,
		gcTime: (import.meta.env.VITE_QUERY_GC_TIME || 30) * 60 * 1000,
		// AAR-1483: kein refetchOnWindowFocus-Override mehr — Tabellen nehmen an
		// der globalen Refetch-Policy teil (Fokus-Refetch ist hier neu;
		// Mount/Reconnect waren bereits an). staleTime drosselt.
		refetchOnMount: true,
		placeholderData: keepPreviousData,
		retry: tableOptions.retry,
		retryDelay: tableOptions.retryDelay,
	})
	const isFetching = useIsFetching(queryKey.value)

	// ---------------------------------------------------------------------------
	// All-pages observer (AAR-1482)
	//
	// When preloadAllPages is requested we register a useQueries observer for
	// every page derived from the main query's meta.total.  Observed queries are
	// never GC'd while the component is mounted, Vue's reactivity is native, and
	// stale pages refetch automatically per the shared staleTime/refetchOnMount
	// policy.  On remount the observers re-create themselves — no manual
	// orchestration needed.
	//
	// AAR-1246 (full reload on filter/search/sort/pageSize change) is covered
	// automatically: the queryKey of every page entry contains all those params,
	// so a change causes the computed array to re-derive entirely.  TanStack
	// drops the old observers, creates new ones, and fetches fresh data.
	// ---------------------------------------------------------------------------

	// Flip to true via preloadAllPages(); stays true for the component lifetime.
	const wantAllPages = ref(!!tableOptions.preloadAllPages)

	// Total pages derived purely from the main query's meta — no extra request.
	const totalPages = computed(() => {
		const total = data.value?.meta?.total ?? 0
		return total > 0 ? Math.ceil(total / itemsPerPage.value) : 0
	})

	// Build one options object per page.  The queryFn captures params at
	// array-build time so a late-running fetch never uses a different page's
	// params (same discipline as prefetchPage).
	const pageQueriesOptions = computed(() => {
		if (!wantAllPages.value || totalPages.value === 0 || !isEnabled()) {
			return []
		}
		// Capture silentError at array-build time alongside params so a late-running
		// queryFn always uses the value that was active when the observer was created.
		const silentReqConfig = tableOptions.silentError
			? { meta: { silentError: true } }
			: undefined
		return Array.from({ length: totalPages.value }, (_, i) => {
			const pageNum = i + 1
			// Freeze params at array-build time — do not close over queryParams.
			const pageParams = { ...queryParams.value, pageNum }
			return {
				queryKey: createPageQueryKey(pageNum) as QueryKey,
				queryFn: () =>
					fetchCollectionData(endpointUsed.value, pageParams, silentReqConfig),
				staleTime: (import.meta.env.VITE_QUERY_STALE_TIME || 5) * 60 * 1000,
				gcTime: (import.meta.env.VITE_QUERY_GC_TIME || 30) * 60 * 1000,
				refetchOnMount: true,
				enabled: isEnabled,
				persister:
					tableOptions.usePersister === true || !isOnline.value
						? persister.persisterFn
						: undefined,
				retry: tableOptions.retry,
				retryDelay: tableOptions.retryDelay,
				// placeholderData intentionally omitted on page queries: keepPreviousData
				// on individual pages would serve the previous page's data under a new key,
				// which would corrupt the combined dataset during filter/search changes.
			}
		})
	})

	// useQueries returns Readonly<Ref<Array<QueryObserverResult>>>.
	// Passing queries as a getter function (() => pageQueriesOptions.value) is
	// the correct reactive pattern for a dynamic-length array in TanStack v5.
	// Verified against the installed @tanstack/vue-query 5.100.14 (not 5.90.7 as
	// stated in the brief): useQueries.js resolves
	// `typeof queries === 'function' ? queries() : queries` inside a computed.
	const allPageQueries = useQueries({
		queries: () => pageQueriesOptions.value,
	})

	// All pages have settled (success or error) — used to drive allPagesLoaded.
	const allPagesSettled = computed(
		() =>
			wantAllPages.value &&
			totalPages.value > 0 &&
			allPageQueries.value.length > 0 &&
			allPageQueries.value.every((r) => r.isSuccess || r.isError)
	)

	// Flag to track whether all pages are loaded (kept as a ref — it is returned).
	const allPagesLoaded = ref(false)

	watch(
		allPagesSettled,
		(v) => {
			allPagesLoaded.value = v
		},
		{ immediate: true }
	)

	// ---------------------------------------------------------------------------
	// queryDataCombined
	//
	// When all-pages mode is active, derive from the live useQueries observers so
	// that reactivity is native and GC'd pages cannot produce phantom data.
	// When all-pages mode is inactive, keep the existing getQueriesData() scan
	// for opportunistic multi-page combines on non-preload tables.
	// ---------------------------------------------------------------------------
	const queryDataCombined = computed<
		Api.Collection<unknown> | undefined | null
	>(() => {
		// Use the observer-based path only when this composable has active page
		// observers of its own (wantAllPages=true AND isEnabled()=true).
		//
		// When wantAllPages is true but allPageQueries is empty — e.g. a disabled
		// shadow-table that shares another table's tableId (AccountExpandableItem
		// pattern) — fall through to the getQueriesData() cache scan so the shadow
		// composable can still read the parent table's preloaded pages.  Without
		// this guard the wantAllPages branch returns undefined (no observers → no
		// data), breaking goToItem's findIndex search on the combined dataset.
		if (wantAllPages.value && allPageQueries.value.length > 0) {
			// Pull data from the observed page queries in page-index order.
			const pagesWithData = allPageQueries.value
				.map((r) => r.data as Api.Collection<unknown> | undefined)
				.filter((d): d is Api.Collection<unknown> => !!d?.data)

			if (pagesWithData.length === 0) {
				// No page data yet; fall through to undefined so itemsTotal falls back
				// to items (main query data / keepPreviousData placeholder).
				return undefined
			}

			const combined = pagesWithData.reduce<Api.Collection<unknown>>(
				(acc, curr) => {
					if (Array.isArray(curr.data)) {
						acc.data.push(...curr.data)
					}
					return acc
				},
				{ data: [], meta: pagesWithData[0].meta, links: pagesWithData[0].links }
			)
			return cloneDeep(combined)
		}

		// Non-preload path: opportunistic combine via cache scan (original logic).
		// isFetching.value touch makes Vue track changes to query fetching status.
		void isFetching.value
		const queryKeyTotal: QueryKey = cloneDeep(queryKey.value)
		delete queryKeyTotal[1].pageNum
		if (!endpointUsed.value) {
			return null
		}
		const queriesData = queryClient.getQueriesData({
			queryKey: queryKeyTotal,
		})
		// Nachgeladene Seiten können in beliebiger Reihenfolge im Cache landen: nach Seitennummer ordnen
		const orderedQueries = tableOptions.dedupeById
			? [...queriesData].sort(
					(a, b) =>
						((a[0] as any)[1]?.pageNum ?? 0) - ((b[0] as any)[1]?.pageNum ?? 0)
				)
			: queriesData
		const matchingData = orderedQueries.map(([, data]) => data).flat()
		if (matchingData.length) {
			const seen = new Set<unknown>()
			const data = matchingData.reduce(
				(acc, curr) => {
					if (Array.isArray(curr?.data)) {
						for (const row of curr.data) {
							if (tableOptions.dedupeById && row?.id != null) {
								if (seen.has(row.id)) continue
								seen.add(row.id)
							}
							acc.data.push(row)
						}
					}
					return acc
				},
				{ data: [], meta: matchingData[0]?.meta }
			)
			return cloneDeep(data)
		}
		return undefined
	})

	// Computed properties for table data
	const items = computed(() => {
		if (tableOptions.propData?.value) {
			return paginatedPropData.value
		}
		return data.value?.data || []
	})
	const meta = computed(() => {
		if (tableOptions.propData?.value) {
			//TODO: return aggregated propData meta
			return {}
		}
		return data.value?.meta || {}
	})
	const itemsTotal = computed(
		() => queryDataCombined.value?.data || items.value
	)
	const itemsLoadedLength = computed(
		() => queryDataCombined.value?.data.length || 0
	)
	const itemsTotalLength = computed(
		() => queryDataCombined.value?.meta?.total || data.value?.meta?.total || 0
	)

	const itemCount = computed(() => {
		if (tableOptions.propData?.value) {
			return propDataTotal.value
		}
		const total = data.value?.meta?.total

		if (typeof total === 'undefined') {
			return queryDataCombined.value?.meta?.total || 0
		}
		return total
	})

	const isLoading = computed(() => {
		return tableOptions.propData?.value
			? false
			: isInitialLoading.value || fetchStatus.value === 'fetching'
	})

	const isUpdating = computed(() =>
		tableOptions.propData?.value
			? false
			: fetchStatus.value === 'fetching' &&
				typeof data.value?.data !== 'undefined'
	)

	// Watch for query errors
	watch(queryError, (newError) => {
		if (newError) {
			error.value = newError
		}
	})

	watch(
		() => tableOptions.tableId?.value,
		async (current, previous) => {
			if (current || !!previous) {
				return
			}
			enable()
			await refetch()
		}
	)

	// Make sure table state is properly saved with initial values
	onMounted(async () => {
		// Force initial data load if needed
		if (
			endpointUsed.value &&
			!tableOptions.propData?.value &&
			!data.value &&
			!tableOptions.preloadAllPages &&
			!isLoading.value &&
			isOnline.value
		) {
			await refetch()
		}

		// The preloadAllPages branch is intentionally removed: wantAllPages is set
		// at construction time (!!tableOptions.preloadAllPages), so pageQueriesOptions
		// already derives its array on the first render and observers self-register.
		// No manual trigger needed on mount.

		await Promise.resolve()
	})

	// Function to prefetch a specific page
	const prefetchPage = async (pageNumber: number) => {
		if (!endpointUsed.value || tableOptions.propData?.value) {
			return Promise.resolve()
		}

		// Skip if trying to prefetch the current page (which is already loaded)
		if (pageNumber === page.value) {
			return Promise.resolve()
		}

		// Create a new params object for the target page
		const pageParams = {
			...queryParams.value,
			pageNum: pageNumber,
		}

		// Use the correct query key format that matches our main query
		const pageQueryKey = createPageQueryKey(pageNumber)

		return await queryClient.prefetchQuery({
			queryKey: pageQueryKey,
			persister:
				tableOptions.usePersister === true || !isOnline.value
					? persister.persisterFn
					: undefined,
			queryFn: () => fetchCollectionData(endpointUsed.value, pageParams),
			staleTime: 5 * 60 * 1000,
		})
	}

	// Function to preload all pages
	//
	// Flips wantAllPages = true so the useQueries observer array activates, then
	// awaits settlement.  On subsequent calls (e.g. goToItem) wantAllPages is
	// already true and totalPages is already known, so the wait resolves quickly.
	const preloadAllPages = async () => {
		if (!endpointUsed.value || tableOptions.propData?.value) {
			return Promise.resolve()
		}

		wantAllPages.value = true

		await new Promise<void>((resolve) => {
			// Resolve immediately when there is nothing to load.
			if ((data.value?.meta?.total ?? 0) === 0 && !isInitialLoading.value) {
				resolve()
				return
			}

			// Fast path: pages have already settled before this call (e.g. goToItem
			// on a table where a page errored — allPagesSettled is true while
			// itemsLoadedLength < itemsTotalLength). Skips installing the watcher.
			if (allPagesSettled.value) {
				resolve()
				return
			}

			// Resolve as soon as all observed page queries have settled, when the
			// main query settles and reports zero total items, OR when the table
			// is/becomes disabled (pageQueriesOptions collapses to [] so no
			// observers will ever settle — e.g. a disabled table that still has
			// cached data, which passes both short-circuits above).
			// `stop` must be a pre-declared `let`: with { immediate: true } the
			// callback runs synchronously inside watch(), where a `const stop`
			// binding would still be in its temporal dead zone — optional chaining
			// does not guard against that ReferenceError. The `resolved` flag lets
			// us stop the watcher right after installation when the immediate tick
			// already resolved.
			let resolved = false
			// Merging declaration and assignment back into `const` would
			// reintroduce the TDZ described above.
			let stop: (() => void) | undefined
			// eslint-disable-next-line prefer-const
			stop = watch(
				[
					allPagesSettled,
					isInitialLoading,
					() => data.value?.meta?.total ?? 0,
					() => pageQueriesOptions.value.length,
				] as const,
				([settled, loading, total, activePageCount]) => {
					if (
						settled ||
						(!loading && total === 0) ||
						// Disabled: wantAllPages is true but the observer array is
						// empty — nothing will ever settle.
						(wantAllPages.value && activePageCount === 0 && !loading)
					) {
						resolved = true
						stop?.()
						resolve()
					}
				},
				{ immediate: true }
			)
			if (resolved) {
				stop()
			}
		})
	}

	// Prefetch adjacent pages for smoother user experience
	async function prefetchAdjacentPages() {
		if (!endpointUsed.value || tableOptions.propData?.value) return

		// Current page is already cached by primary query
		const currentPage = page.value

		// Prefetch previous page if we're not on the first page
		if (currentPage > 1) {
			prefetchPage(currentPage - 1)
		}

		// Calculate max pages and prefetch next page if not on last page
		const maxPages = Math.ceil(
			(data.value?.meta?.total || 0) / itemsPerPage.value
		)
		if (currentPage < maxPages) {
			prefetchPage(currentPage + 1)
		}
	}

	// New method: Prefetch next page without changing the current page
	async function prefetchNextPage() {
		if (!endpointUsed.value || tableOptions.propData?.value) {
			return Promise.resolve()
		}

		const currentPage = page.value
		const maxPages = Math.ceil(
			(data.value?.meta?.total || 0) / itemsPerPage.value
		)
		// Check if there is a next page
		if (currentPage < maxPages) {
			return await prefetchPage(currentPage + 1)
		}

		// Return null if there's no next page
		return Promise.resolve(null)
	}

	// Event handlers
	function handlePageChange(newPage: number) {
		page.value = newPage
	}

	function handleItemsPerPageChange(value: number) {
		itemsPerPage.value = value
		page.value = 1

		// pageSize is embedded in every page query key so the useQueries array
		// re-derives automatically when itemsPerPage changes.  No manual refetch
		// or preloadAllPages call needed for preload tables.
		// For non-preload tables a refetch() is not needed here either: TanStack
		// will refetch the main query because queryKey (which contains pageSize)
		// changed.
	}

	function handleSortChange(value: SortItem[]) {
		sortByValues.value = value
		page.value = 1
	}

	function handleSearch(value: string) {
		search.value = value
		page.value = 1
	}

	/**
	 * Reloads the data from the server or refreshes the prop data view
	 * @param {boolean} keepCurrentData - If true, keeps current data visible while fetching new data
	 * @returns {Promise<any>} - Promise that resolves when data is reloaded
	 */
	async function reload(keepCurrentData = false) {
		page.value = 1
		if (!endpointUsed.value) {
			return Promise.resolve(null)
		}
		// invalidateTableCache uses a ['collection', endpoint] key which does not
		// match the module-scoped keys used here, so it has no practical effect on
		// this composable's queries.  It is kept for any external cache consumers
		// that may depend on it.
		tableStateStore.invalidateTableCache(tableId.value, endpointUsed.value)
		allPagesLoaded.value = false

		if (keepCurrentData) {
			const queryOptions = {
				queryKey: createPageQueryKey(1),
				queryFn: () =>
					fetchCollectionData(endpointUsed.value, queryParams.value),
				staleTime: 0,
				placeholderData: data.value,
			}

			await queryClient.fetchQuery(queryOptions)

			if (wantAllPages.value) {
				// invalidateData marks all page queries stale; their active observers
				// (registered by useQueries) refetch automatically.
				await invalidateData()
			}
		} else if (wantAllPages.value) {
			// Invalidate all page queries for this endpoint — active observers refetch
			// and the promise resolves when they settle.
			await invalidateData()
		} else {
			await refetch()
		}
	}

	function setSelectedItems(items: any[]) {
		selectedItems.value = items
	}

	function updateFilter(filter: FilterDefinition, value: any) {
		const filterList = [...filters.value]
		const index = filterList.findIndex(
			(f) => f.definition.component === filter.component
		)

		if (index !== -1) {
			filterList[index] = { ...filterList[index], value }
		} else {
			filterList.push({ definition: filter, value })
		}

		filters.value = filterList
		page.value = 1
	}

	async function invalidateData(options?: { ignoreEndpoint?: boolean }) {
		if (!module.value) {
			return Promise.resolve(`${module.value} is not defined`)
		}

		if (endpointUsed.value && !options?.ignoreEndpoint) {
			// If we have an endpoint, be more specific about what to invalidate
			return await queryClient.invalidateQueries({
				predicate: (query) => {
					if (query.queryKey[0] !== module.value) return false

					if (
						query.queryKey.length >= 2 &&
						typeof query.queryKey[1] === 'object' &&
						query.queryKey[1].endpoint
					) {
						const queryEndpoint = query.queryKey[1].endpoint
						return (
							queryEndpoint === endpointUsed.value ||
							(typeof queryEndpoint === 'string' &&
								(queryEndpoint.includes(endpointUsed.value) ||
									endpointUsed.value.includes(queryEndpoint)))
						)
					}
					return false
				},
			})
		} else {
			// No endpoint, invalidate all queries for this module
			return await queryClient.invalidateQueries({
				queryKey: [module.value],
				exact: false,
			})
		}
	}

	async function goToItem(needle: any, key: string = 'id') {
		if (!needle) {
			return false
		}
		const foundItemIndex = items.value.findIndex(
			(item) => item[key] === needle[key]
		)
		if (foundItemIndex !== -1) {
			return items.value[foundItemIndex]
		}

		// AAR-178: Nicht auf der aktuellen Seite → immer erst alle Seiten laden.
		// Der fruehere Abbruch `itemsLoadedLength < itemsTotalLength` war truegerisch:
		// ohne aktive Seiten-Observer zaehlt queryDataCombined per Cache-Scan auch
		// Nachbartabellen desselben Endpunkts mit (z.B. mit und ohne `count=`),
		// meldete 50 von 30 „geladen" und fand die Buchung auf Seite 2 nie.
		// preloadAllPages ist idempotent und bei gesetzten Observern sofort fertig.
		await preloadAllPages()

		const foundTotalItemIndex = itemsTotal.value.findIndex(
			(item) => item[key] === needle[key]
		)
		if (foundTotalItemIndex !== -1) {
			page.value = Math.floor(foundTotalItemIndex / itemsPerPage.value) + 1
			return itemsTotal.value[foundTotalItemIndex]
		}

		return false
	}

	function isEnabled() {
		return (
			!!endpointUsed.value &&
			!tableOptions.propData?.value &&
			(typeof tableOptions.disabled === 'undefined' ||
				tableOptions.disabled?.value === false)
		)
	}

	async function enable() {
		if (tableOptions.disabled?.value) {
			tableOptions.disabled = computed(() => false)
			await refetch()
		}
		return Promise.resolve()
	}

	function init() {
		if (tableOptions.disabled?.value) {
			return
		}
		tableStateStore.getOrCreateTableState(tableId.value, {
			page: initialState.page,
			itemsPerPage: initialState.itemsPerPage,
			search: initialState.search,
			sortBy: initialState.sortBy,
			expandedItems: initialState.expandedItems,
			selectedItems: initialState.selectedItems,
			filters: initialState.filters,
			endpoint: endpointUsed.value,
		})
	}

	return {
		// Table identification
		tableId,

		// State
		page,
		itemsPerPage,
		search,
		sortByValues,
		sortOrder,
		sortField,
		filters,
		expandedItems,
		selectedItems,
		hasSelectedItems,
		itemsLoadedLength,
		itemsTotalLength,
		usePlaceHolderData,
		options: tableOptions,

		// Data
		meta,
		items,
		itemsTotal,
		itemCount,
		isLoading,
		isInitialLoading,
		isPlaceholderData,
		isUpdating,
		error,
		allPagesLoaded,
		fetchPromise,

		// Actions
		enable,
		handlePageChange,
		handleItemsPerPageChange,
		handleSortChange,
		handleSearch,
		prefetchAdjacentPages,
		prefetchNextPage,
		prefetchPage,
		preloadAllPages,
		reload,
		setSelectedItems,
		updateFilter,
		invalidateData,
		goToItem,

		// Direct access to the query
		queryKey,
		queryData: data,
		queryRefetch: refetch,

		// Store access for advanced usage
		tableStateStore,
	}
}
